import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/shared/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { dbg, dbgGroup } from "@/lib/rec-debug";
import { Calendar, User, Phone, Mail } from "lucide-react";
import Icon from '@mdi/react';
import { mdiPhoneRotatePortrait, mdiPhoneRotateLandscape } from '@mdi/js';

const VIDDBG =
  typeof window !== "undefined" &&
  (new URLSearchParams(window.location.search).get("recDebug") === "1" ||
    new URLSearchParams(window.location.search).get("debug") === "1" ||
    localStorage.getItem("recDebug") === "1");

interface VideoPlayerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videoUrl: string;
  storagePath?: string;
  callDetails: {
    customerName: string;
    customerEmail?: string;
    customerPhone?: string;
    startTime?: string;
    duration?: number;
    type?: string;
    notes?: string;
  };
}

export function VideoPlayerModal({
  open,
  onOpenChange,
  videoUrl,
  storagePath,
  callDetails,
}: VideoPlayerModalProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const sessionIdRef = useRef((typeof crypto !== "undefined" && crypto.randomUUID?.()) ?? String(Math.random()).slice(2));
  const sessionId = sessionIdRef.current;

  const log = (tag: string, data?: unknown) => {
    if (!VIDDBG) return;
    console.log(`[RECDbg][VideoPlayerModal][${sessionId}][${Math.round(performance.now())}ms] ${tag}`, data ?? "");
  };

  // ── Rotation ─────────────────────────────────────────────────────────────────
  const [rotation, setRotation] = useState(0);
  // Mirror rotation into a ref so the ResizeObserver closure always reads the
  // current value without needing to be recreated on every rotate press.
  const rotationRef = useRef(rotation);
  useEffect(() => { rotationRef.current = rotation; }, [rotation]);

  // ── Anchor-axis fit sizing ────────────────────────────────────────────────────
  // containerRef: the black area that wraps the <video>.
  const containerRef = useRef<HTMLDivElement>(null);
  // Intrinsic video dimensions — stored in a ref so updates don't trigger renders.
  const intrinsicRef = useRef<{ w: number; h: number } | null>(null);
  // Last-measured container dimensions — same rationale.
  const containerDimsRef = useRef<{ w: number; h: number } | null>(null);
  const lastResizeLogRef = useRef<{ w: number; h: number } | null>(null);
  // Computed display size — drives a single targeted re-render.
  const [videoSize, setVideoSize] = useState<{ width: number; height: number } | null>(null);

  // Core computation — maximize video size to fill container (cover when space allows):
  //
  //   rot 90|270 → effective dims swap (the video appears transposed on screen)
  //   Use the larger of scaleByW and scaleByH so the video fills the container.
  //   Landscape videos in portrait containers (or vice versa) scale up and may crop edges.
  //
  //   Use 0.98 fill factor. objectFit: contain on the video clips overflow.
  const FILL_FACTOR = 0.98;
  const applyFit = useCallback((cW: number, cH: number, vW: number, vH: number, rot: number) => {
    if (!cW || !cH || !vW || !vH) return;

    // Effective visual dimensions after CSS rotation
    const effW = (rot === 90 || rot === 270) ? vH : vW;
    const effH = (rot === 90 || rot === 270) ? vW : vH;

    // Cover: use larger scale so video fills container (scale up when space allows)
    const scaleByW = (cW * FILL_FACTOR) / effW;
    const scaleByH = (cH * FILL_FACTOR) / effH;
    let scale = Math.max(scaleByW, scaleByH);

    // Safety clamp: cap at 0.99 so we don't overflow
    const maxScale = Math.min((cW * 0.99) / effW, (cH * 0.99) / effH);
    const clamped = scale > maxScale;
    if (clamped) scale = maxScale;

    const finalW = Math.round(vW * scale);
    const finalH = Math.round(vH * scale);

    log("applyFit", {
      containerDims: { cW, cH },
      intrinsicDims: { vW, vH },
      rotation: rot,
      effW,
      effH,
      scaleByW,
      scaleByH,
      scale,
      clamped,
      finalVideoSize: { width: finalW, height: finalH },
    });

    // Set the element's pre-rotation box dimensions (preserves aspect ratio)
    setVideoSize({ width: finalW, height: finalH });
  }, []);

  // ResizeObserver: recompute whenever the container changes size (window resize,
  // panel toggle, etc.).  Disconnect + reset on modal close.
  useEffect(() => {
    if (!open) {
      setVideoSize(null);
      intrinsicRef.current = null;
      containerDimsRef.current = null;
      return;
    }

    const el = containerRef.current;
    if (!el) return;

    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      containerDimsRef.current = { w: width, h: height };
      // C2) Throttle: only log when dims change meaningfully (>5px)
      const prev = lastResizeLogRef.current;
      const meaningfulChange = !prev || Math.abs(prev.w - width) > 5 || Math.abs(prev.h - height) > 5;
      if (meaningfulChange) {
        lastResizeLogRef.current = { w: width, h: height };
        log("ResizeObserver", {
          containerDims: { w: width, h: height },
          rotation: rotationRef.current,
        });
      }
      if (intrinsicRef.current) {
        applyFit(width, height, intrinsicRef.current.w, intrinsicRef.current.h, rotationRef.current);
      }
    });
    ro.observe(el);

    // Immediate measurement so that if loadedmetadata has already fired (e.g. cached
    // video or very fast network), the size is correct without waiting for the first
    // ResizeObserver callback.
    const rect = el.getBoundingClientRect();
    if (rect.width && rect.height) {
      containerDimsRef.current = { w: rect.width, h: rect.height };
      if (intrinsicRef.current) {
        applyFit(rect.width, rect.height, intrinsicRef.current.w, intrinsicRef.current.h, rotationRef.current);
      }
    }

    return () => ro.disconnect();
  }, [open, applyFit]);

  // loadedmetadata: record intrinsic dimensions. After rAF, check if rendered aspect is inverted
  // (e.g. rotation metadata in MP4) and treat intrinsic as swapped for sizing.
  const handleLoadedMetadata = useCallback(() => {
    const vid = videoRef.current;
    if (!vid) return;
    const vW = vid.videoWidth;
    const vH = vid.videoHeight;

    log("loadedmetadata_intrinsic", {
      videoWidth: vW,
      videoHeight: vH,
      container: containerDimsRef.current,
    });

    intrinsicRef.current = { w: vW, h: vH };
    if (containerDimsRef.current) {
      applyFit(containerDimsRef.current.w, containerDimsRef.current.h, vW, vH, rotationRef.current);
    }
    // After first paint, re-check: if rendered aspect differs from intrinsic, video has rotation metadata
    requestAnimationFrame(() => {
      const vid2 = videoRef.current;
      if (!vid2 || !containerDimsRef.current) return;
      const rect = vid2.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        const renderedIsPortrait = rect.height > rect.width;
        const intrinsicIsPortrait = vH > vW;
        const swapApplied = intrinsicIsPortrait !== renderedIsPortrait;

        log("loadedmetadata_rAF", {
          renderedRect: { w: rect.width, h: rect.height },
          renderedIsPortrait,
          intrinsicIsPortrait,
          swapApplied,
          finalIntrinsic: swapApplied ? { w: vH, h: vW } : { w: vW, h: vH },
        });

        if (swapApplied) {
          intrinsicRef.current = { w: vH, h: vW };
          applyFit(
            containerDimsRef.current.w,
            containerDimsRef.current.h,
            vH,
            vW,
            rotationRef.current,
          );
        }
      }
    });
  }, [applyFit]);

  // Clear URL params when modal closes
  useEffect(() => {
    if (!open) {
      const searchParams = new URLSearchParams(location.search);
      if (searchParams.has('recording')) {
        navigate(location.pathname, { replace: true });
      }
    }
  }, [open, navigate, location]);

  // Reset rotation only when modal opens (not on each rotate press)
  useEffect(() => {
    if (open) {
      setRotation(0);
      const t = setTimeout(() => {
        const container = containerRef.current;
        const rect = container?.getBoundingClientRect();
        log("modal_opened", {
          containerRect: rect ? { w: rect.width, h: rect.height } : "n/a",
          viewport: { w: window.innerWidth, h: window.innerHeight },
          videoUrlId: videoUrl?.slice(-24) ?? "n/a",
          storagePathId: storagePath?.slice(-32) ?? "n/a",
        });
      }, 50);
      return () => clearTimeout(t);
    }
  }, [open, videoUrl, storagePath]);

  // Recompute sizing immediately when the user rotates the video, because the
  // anchor axis flips between portrait and landscape at 90°/270°.
  useEffect(() => {
    if (!intrinsicRef.current || !containerDimsRef.current) return;
    applyFit(
      containerDimsRef.current.w,
      containerDimsRef.current.h,
      intrinsicRef.current.w,
      intrinsicRef.current.h,
      rotation,
    );
  }, [rotation, applyFit]);

  const handleRotateRight = () => {
    setRotation((prev) => {
      const next = (prev + 90) % 360;
      console.log('🔄 [VideoPlayerModal] Rotating video clockwise:', { from: prev, to: next });
      return next;
    });
  };

  const handleRotateLeft = () => {
    setRotation((prev) => {
      const next = (prev - 90 + 360) % 360;
      console.log('🔄 [VideoPlayerModal] Rotating video counter-clockwise:', { from: prev, to: next });
      return next;
    });
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] sm:w-[90vw] max-h-[95vh] sm:max-h-[90vh] h-[95dvh] sm:h-[90dvh] p-0 overflow-hidden flex flex-col">
        <VisuallyHidden>
          <DialogTitle>Call Recording - {callDetails.customerName}</DialogTitle>
          <DialogDescription>
            Video recording from call with {callDetails.customerName} on {formatDate(callDetails.startTime)}
          </DialogDescription>
        </VisuallyHidden>
        <div className="flex flex-col sm:flex-row flex-1 min-h-0 overflow-hidden">
          {/* Video Player - 80% on desktop, flexible on mobile */}
          <div className="flex-[8] min-h-0 bg-black flex flex-col items-center justify-center relative overflow-hidden p-3 sm:p-4">
            {/*
              containerRef measures the available area for the video.
              flex-1 makes it fill the space above the rotation controls.
              The <video> is given explicit pixel dimensions (70 % fit) so it never
              occupies the full black area — it sits centered with breathing room.
            */}
            <div
              ref={containerRef}
              className="flex-1 min-h-0 w-full flex items-center justify-center min-w-0"
            >
              <video
                ref={videoRef}
                src={videoUrl}
                controls
                playsInline
                controlsList="nodownload"
                className="max-w-full max-h-full"
                style={{
                  width: videoSize?.width,
                  height: videoSize?.height,
                  // Lock aspect ratio to prevent squishing (critical for landscape videos)
                  aspectRatio: videoSize ? `${videoSize.width} / ${videoSize.height}` : undefined,
                  transform: `rotate(${rotation}deg)`,
                  transition: 'transform 300ms ease-in-out',
                  objectFit: 'contain',
                }}
                onLoadedMetadata={handleLoadedMetadata}
              />
            </div>

            {/* Rotation controls - always visible below the video */}
            <div
              className="flex-shrink-0 flex gap-4 justify-end items-center py-3 sm:py-4 pr-2 sm:pr-4"
            >
              {/* Rotate Left (Counter-clockwise) */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🔘 [VideoPlayerModal] Rotate LEFT button clicked!');
                  handleRotateLeft();
                }}
                className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-2 transition-all hover:scale-105 active:scale-95 cursor-pointer px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20"
                title="Rotate video 90° counter-clockwise"
                type="button"
              >
                <Icon
                  path={mdiPhoneRotatePortrait}
                  size={1}
                  color="white"
                  className="drop-shadow-lg shrink-0"
                />
                <span className="text-white text-xs font-semibold whitespace-nowrap drop-shadow-lg">
                  Rotate Left
                </span>
              </button>

              {/* Rotate Right (Clockwise) */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('🔘 [VideoPlayerModal] Rotate RIGHT button clicked!');
                  handleRotateRight();
                }}
                className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-2 transition-all hover:scale-105 active:scale-95 cursor-pointer px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20"
                title="Rotate video 90° clockwise"
                type="button"
              >
                <Icon
                  path={mdiPhoneRotateLandscape}
                  size={1}
                  color="white"
                  className="drop-shadow-lg shrink-0"
                />
                <span className="text-white text-xs font-semibold whitespace-nowrap drop-shadow-lg">
                  Rotate Right
                </span>
              </button>
            </div>
          </div>

          {/* Info Panel - 20% on desktop, scrollable on mobile */}
          <div className="flex-[2] min-h-0 min-w-[200px] sm:min-w-[220px] bg-gray-50 dark:bg-gray-900 p-4 sm:p-6 pb-8 sm:pb-6 overflow-y-auto border-t sm:border-t-0 sm:border-l flex-shrink-0">
            <div className="space-y-6 pb-4">
              <div>
                <h3 className="text-lg font-semibold mb-4">Call Details</h3>
              </div>

              {/* Customer Name */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <User className="h-4 w-4" />
                  <span>Customer</span>
                </div>
                <p className="text-sm font-medium">{callDetails.customerName}</p>
              </div>

              {/* Email */}
              {callDetails.customerEmail && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    <span>Email</span>
                  </div>
                  <p className="text-sm font-medium break-all">
                    {callDetails.customerEmail}
                  </p>
                </div>
              )}

              {/* Phone */}
              {callDetails.customerPhone && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    <span>Phone</span>
                  </div>
                  <p className="text-sm font-medium">{callDetails.customerPhone}</p>
                </div>
              )}

              {/* Date/Time */}
              {callDetails.startTime && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>Date & Time</span>
                  </div>
                  <p className="text-sm font-medium">
                    {formatDate(callDetails.startTime)}
                  </p>
                </div>
              )}

              {/* Call Type */}
              {callDetails.type && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>Type</span>
                  </div>
                  <p className="text-sm font-medium capitalize">
                    {callDetails.type.replace(/-/g, " ")}
                  </p>
                </div>
              )}

              {/* Notes */}
              {callDetails.notes && (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>Notes</span>
                  </div>
                  <p className="text-sm font-medium whitespace-pre-wrap">
                    {callDetails.notes}
                  </p>
                </div>
              )}

              {/* Storage Path */}
              {storagePath && (
                <div className="space-y-1 pt-4 border-t">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>Storage Path</span>
                  </div>
                  <p className="text-xs font-mono text-gray-600 break-all">
                    {storagePath}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
